import { Armchair } from "lucide-react";

import { Label } from "components/ui/label";

import type { Seating } from "../../seatings/schemas";
import { SEATINGS_ENABLED } from "../../seatings/seatings-enabled";
import { templateKeys } from "../announcement-placeholders";
import { WARNING_BOX } from "./WarningNote";

export const NO_SEATING = "";

/** Whether a title or body fills in {dinner_table}, so it needs a seating. */
export function usesDinnerTable(title: string, body: string): boolean {
  return (
    templateKeys(title).has("dinner_table") ||
    templateKeys(body).has("dinner_table")
  );
}

/**
 * What stands in the way of the chosen seating at the send time, or null.
 *
 * The backend refuses {dinner_table} without a seating, and refuses to send
 * when the seating is gone or not published (seating_unsendable_reason in
 * event_announcement.py). Send now can be judged here; Send later only
 * warns, because the organiser may publish before the send time.
 */
export function seatingProblem(
  seatings: Seating[] | null,
  value: string,
  mode: "now" | "later",
): string | null {
  if (value === NO_SEATING)
    return "Choose which seating {dinner_table} is filled from.";
  if (seatings === null) return null;
  const seating = seatings.find((item) => item.id === value);
  if (!seating)
    return "This seating no longer exists. Choose another one, or remove {dinner_table}.";
  if (seating.status !== "PUBLISHED")
    return mode === "now"
      ? "This seating is not published, so nobody has a dinner table yet and the announcement would not be sent." +
          (SEATINGS_ENABLED
            ? " Publish it in the Seatings tab first."
            : " Dinner tables are switched off at the moment.")
      : "This seating is not published yet. Publish it before the send time, or the announcement is not sent.";
  return null;
}

/**
 * Which dinner seating a {dinner_table} placeholder reads from (TNG-94).
 * Shown as soon as the message uses it, and required from then on.
 */
export function SeatingPicker({
  id,
  testId,
  seatings,
  value,
  onChange,
  mode,
  disabled,
}: {
  id: string;
  testId: string;
  /** null while loading. */
  seatings: Seating[] | null;
  value: string;
  onChange: (seatingId: string) => void;
  mode: "now" | "later";
  disabled?: boolean;
}) {
  const known =
    value === NO_SEATING ||
    seatings === null ||
    seatings.some((item) => item.id === value);
  const problem =
    value === NO_SEATING ? null : seatingProblem(seatings, value, mode);
  const hintId = `${id}-hint`;

  return (
    <div className="space-y-1.5">
      <Label htmlFor={id} className="flex items-center gap-2">
        <Armchair className="h-4 w-4 shrink-0" />
        Seating for {"{dinner_table}"}
      </Label>
      <select
        id={id}
        data-testid={testId}
        value={value}
        disabled={disabled}
        required
        aria-describedby={hintId}
        onChange={(event) => onChange(event.target.value)}
        className="min-h-11 w-full rounded-md border border-input bg-background px-3 text-base text-foreground disabled:cursor-not-allowed disabled:opacity-50 sm:min-h-9 sm:text-sm"
      >
        <option value={NO_SEATING}>Choose a seating</option>
        {!known && <option value={value}>Deleted seating</option>}
        {(seatings ?? []).map((seating) => (
          <option key={seating.id} value={seating.id}>
            {seating.name}
            {seating.status === "PUBLISHED" ? "" : " (not published)"}
          </option>
        ))}
      </select>
      {problem ? (
        <p
          id={hintId}
          role="status"
          className={WARNING_BOX}
          data-testid={`${testId}-warning`}
        >
          {problem}
        </p>
      ) : (
        <p
          id={hintId}
          className="text-xs text-muted-foreground"
          data-testid={`${testId}-hint`}
        >
          {seatings !== null && seatings.length === 0
            ? SEATINGS_ENABLED
              ? "This event has no seatings yet. Add one in the Seatings tab."
              : "Dinner tables are switched off at the moment, so {dinner_table} would stay empty."
            : "Each guest sees their dinner table in this seating. Guests without one see the fallback."}
        </p>
      )}
    </div>
  );
}
